import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LanguageService } from '../../../../services/i18n/language';
import { DictionaryService, CountryDto } from '../../../../services/api/dictionary';
import { RegistrationService, SupplierInvitePreview } from '../../../../services/api/registration';
import { CustomsCalculationsService } from '../../../../services/api/customs-calculations';
import { AuthService } from '../../../../services/auth/auth';
import { CountrySelectComponent } from '../../../shared/country-select/country-select';
import { LegalConsentComponent } from '../../../legal/legal-consent/legal-consent.component';
import { Subscription } from 'rxjs';

/** Ключ, под которым токен приглашения временно хранится, пока пользователь логинится
 *  под уже существующим аккаунтом («У меня уже есть аккаунт»). Читается в LoginComponent. */
export const PENDING_SUPPLIER_INVITE_KEY = 'pendingSupplierInviteToken';

/**
 * Форма регистрации поставщика — используется в двух режимах с ОДНИМ и тем же
 * компонентом (та же форма, тот же UX):
 *  - /invite/supplier/:token — переход по ссылке-приглашению на конкретную перевозку:
 *    показываем маршрут (без финансовых деталей), после регистрации поставщик сразу
 *    привязывается к этой перевозке.
 *  - /registration/supplier — самостоятельная регистрация с главной страницы
 *    ("Я - поставщик"), без токена и без привязки к перевозке.
 * После успешной регистрации в обоих случаях — автоматический вход и переход в
 * кабинет поставщика.
 */
@Component({
  selector: 'app-supplier-invite',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, CountrySelectComponent, LegalConsentComponent],
  templateUrl: './supplier-invite.html',
  styleUrl: './supplier-invite.scss'
})
export class SupplierInviteComponent implements OnInit, OnDestroy {
  /** true — стандартная самостоятельная регистрация без ссылки-приглашения. */
  isStandalone = false;
  token = '';
  preview: SupplierInvitePreview | null = null;
  loadingPreview = true;
  previewError: string | null = null;
  /** true — уже авторизованный поставщик, приглашение принимается автоматически (без формы). */
  acceptingAsLoggedInSupplier = false;

  countries: CountryDto[] = [];
  registrationForm: FormGroup;
  submitting = false;
  error: string | null = null;

  titleInviteText = 'Приглашение поставщика';
  titleStandaloneText = 'Регистрация поставщика';
  titleText = 'Приглашение поставщика';
  requiredText = '*';
  previewCheckingText = 'Проверяем ссылку...';
  acceptingInviteText = 'Принимаем приглашение...';
  standaloneHintText = 'Формируйте инвойсы, упаковочные листы и договор по перевозке — заполните форму, чтобы зарегистрироваться как поставщик.';
  inviteHintText = 'Вас пригласили стать поставщиком по этой перевозке. Заполните форму, чтобы зарегистрироваться.';
  personalInfoText = 'Личная информация';
  emailText = 'Email';
  fullNameText = 'Полное имя';
  phoneText = 'Телефон';
  passwordSectionText = 'Пароль';
  passwordText = 'Пароль';
  confirmPasswordText = 'Подтвердите пароль';
  companySectionText = 'Компания-поставщик';
  companyNameText = 'Название компании';
  countryText = 'Страна';
  selectCountryText = 'Выберите страну';
  fullNamePlaceholderText = 'Имя Фамилия';
  passwordPlaceholderText = 'Минимум 6 символов';
  confirmPasswordPlaceholderText = 'Повторите пароль';
  companyNamePlaceholderText = 'ИП/ТОО «Наименование»';
  submitText = 'Зарегистрироваться как поставщик';
  submittingText = 'Регистрация...';
  hasAccountText = 'Уже есть аккаунт? Войти';
  alreadyHaveAccountButtonText = 'У меня уже есть аккаунт';

  errorRequiredText = 'Это поле обязательно для заполнения';
  errorEmailText = 'Введите корректный email адрес';
  errorMinlengthText = 'Минимальная длина: {min} символов';
  errorMaxlengthText = 'Максимальная длина: {max} символов';
  errorPasswordMismatchText = 'Пароли не совпадают';
  errorWrongRoleText = 'Эта ссылка предназначена для поставщиков. Вы вошли под другой ролью — выйдите из аккаунта, чтобы принять приглашение.';
  errorAlreadyUsedText = 'Эта ссылка-приглашение уже использована другим поставщиком.';
  errorInvalidOrExpiredText = 'Ссылка-приглашение недействительна или устарела.';
  errorPreviewCheckFailedText = 'Не удалось проверить ссылку приглашения';
  errorAcceptInviteFailedText = 'Не удалось принять приглашение';
  errorAutoLoginFailedText = 'Аккаунт создан, но не удалось войти автоматически. Войдите вручную.';
  errorRegistrationDefaultText = 'Ошибка при регистрации';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private languageService: LanguageService,
    private dictionaryService: DictionaryService,
    private registrationService: RegistrationService,
    private customsCalculationsService: CustomsCalculationsService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.registrationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      fullName: ['', [Validators.required, Validators.maxLength(255)]],
      phone: ['', [Validators.maxLength(20)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]],
      confirmPassword: ['', [Validators.required]],
      companyName: ['', [Validators.required, Validators.maxLength(255)]],
      countryId: [null, [Validators.required]],
      acceptTerms: [false, [Validators.requiredTrue]],
      acceptPersonalData: [false, [Validators.requiredTrue]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.isStandalone = !this.token;

    if (this.isStandalone) {
      // Самостоятельная регистрация — предпросмотра перевозки нет, форма доступна сразу.
      this.titleText = this.titleStandaloneText;
      this.loadingPreview = false;
      this.loadCountries();
      return;
    }

    // Уже авторизован? Если это поставщик — просто привязываем к перевозке, без формы
    // регистрации. Если другая роль — регистрация тут не подходит (нужно выйти из аккаунта).
    if (this.authService.isAuthenticated()) {
      const roleCode = this.authService.getUser()?.role?.code;
      if (roleCode === 'ROLE_SUPPLIER') {
        this.acceptAsLoggedInSupplier();
      } else {
        this.loadingPreview = false;
        this.previewError = this.errorWrongRoleText;
      }
      return;
    }

    this.registrationService.getSupplierInvitePreview(this.token).subscribe({
      next: (preview) => {
        this.preview = preview;
        this.loadingPreview = false;
        if (!preview.valid) {
          this.previewError = preview.alreadyUsed ? this.errorAlreadyUsedText : this.errorInvalidOrExpiredText;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingPreview = false;
        this.previewError = this.errorPreviewCheckFailedText;
        this.cdr.markForCheck();
      }
    });

    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.titleInviteText = this.t('registration.supplier.titleInvite', 'Приглашение поставщика');
    this.titleStandaloneText = this.t('registration.supplier.titleStandalone', 'Регистрация поставщика');
    this.titleText = this.isStandalone ? this.titleStandaloneText : this.titleInviteText;
    this.previewCheckingText = this.t('registration.supplier.previewChecking', 'Проверяем ссылку...');
    this.acceptingInviteText = this.t('registration.supplier.acceptingInvite', 'Принимаем приглашение...');
    this.standaloneHintText = this.t('registration.supplier.standaloneHint', 'Формируйте инвойсы, упаковочные листы и договор по перевозке — заполните форму, чтобы зарегистрироваться как поставщик.');
    this.inviteHintText = this.t('registration.supplier.inviteHint', 'Вас пригласили стать поставщиком по этой перевозке. Заполните форму, чтобы зарегистрироваться.');
    this.personalInfoText = this.t('registration.cargoOwner.personalInfo', 'Личная информация');
    this.emailText = this.t('registration.cargoOwner.email', 'Email');
    this.fullNameText = this.t('registration.cargoOwner.fullName', 'Полное имя');
    this.phoneText = this.t('registration.cargoOwner.phone', 'Телефон');
    this.passwordSectionText = this.t('registration.cargoOwner.password', 'Пароль');
    this.passwordText = this.t('registration.cargoOwner.passwordField', 'Пароль');
    this.confirmPasswordText = this.t('registration.cargoOwner.confirmPassword', 'Подтвердите пароль');
    this.companySectionText = this.t('registration.supplier.companySection', 'Компания-поставщик');
    this.companyNameText = this.t('registration.cargoOwner.companyName', 'Название компании');
    this.countryText = this.t('registration.cargoOwner.country', 'Страна');
    this.selectCountryText = this.t('registration.cargoOwner.selectCountry', 'Выберите страну');
    this.fullNamePlaceholderText = this.t('registration.cargoOwner.fullNamePlaceholder', 'Имя Фамилия');
    this.passwordPlaceholderText = this.t('registration.cargoOwner.passwordPlaceholder', 'Минимум 6 символов');
    this.confirmPasswordPlaceholderText = this.t('registration.cargoOwner.confirmPasswordPlaceholder', 'Повторите пароль');
    this.companyNamePlaceholderText = this.t('registration.cargoOwner.companyNamePlaceholder', 'ИП/ТОО «Наименование»');
    this.submitText = this.t('registration.supplier.submit', 'Зарегистрироваться как поставщик');
    this.submittingText = this.t('registration.cargoOwner.submitting', 'Регистрация...');
    this.hasAccountText = this.t('registration.cargoOwner.hasAccount', 'Уже есть аккаунт? Войти');
    this.alreadyHaveAccountButtonText = this.t('registration.supplier.alreadyHaveAccount', 'У меня уже есть аккаунт');

    this.errorRequiredText = this.t('registration.cargoOwner.errors.required', 'Это поле обязательно для заполнения');
    this.errorEmailText = this.t('registration.cargoOwner.errors.email', 'Введите корректный email адрес');
    this.errorMinlengthText = this.t('registration.cargoOwner.errors.minlength', 'Минимальная длина: {min} символов');
    this.errorMaxlengthText = this.t('registration.cargoOwner.errors.maxlength', 'Максимальная длина: {max} символов');
    this.errorPasswordMismatchText = this.t('registration.cargoOwner.errors.passwordMismatch', 'Пароли не совпадают');
    this.errorWrongRoleText = this.t('registration.supplier.errorWrongRole', 'Эта ссылка предназначена для поставщиков. Вы вошли под другой ролью — выйдите из аккаунта, чтобы принять приглашение.');
    this.errorAlreadyUsedText = this.t('registration.supplier.errorAlreadyUsed', 'Эта ссылка-приглашение уже использована другим поставщиком.');
    this.errorInvalidOrExpiredText = this.t('registration.supplier.errorInvalidOrExpired', 'Ссылка-приглашение недействительна или устарела.');
    this.errorPreviewCheckFailedText = this.t('registration.supplier.errorPreviewCheckFailed', 'Не удалось проверить ссылку приглашения');
    this.errorAcceptInviteFailedText = this.t('registration.supplier.errorAcceptInviteFailed', 'Не удалось принять приглашение');
    this.errorAutoLoginFailedText = this.t('registration.supplier.errorAutoLoginFailed', 'Аккаунт создан, но не удалось войти автоматически. Войдите вручную.');
    this.errorRegistrationDefaultText = this.t('registration.supplier.errorRegistrationDefault', 'Ошибка при регистрации');
  }

  private acceptAsLoggedInSupplier(): void {
    this.loadingPreview = false;
    this.acceptingAsLoggedInSupplier = true;
    this.customsCalculationsService.acceptSupplierInvite(this.token).subscribe({
      next: () => {
        this.router.navigate(['/supplier/dashboard']);
      },
      error: (err) => {
        this.acceptingAsLoggedInSupplier = false;
        this.previewError = err.error?.message || this.errorAcceptInviteFailedText;
        this.cdr.markForCheck();
      }
    });
  }

  /** «У меня уже есть аккаунт» — сохраняем токен и уходим на логин; после успешного
   *  входа LoginComponent сам примет это же приглашение и отправит в кабинет поставщика. */
  goToLoginWithExistingAccount(): void {
    sessionStorage.setItem(PENDING_SUPPLIER_INVITE_KEY, this.token);
    this.router.navigate(['/login']);
  }

  private loadCountries(): void {
    this.dictionaryService.getAllCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
        // Казахстан выбран по умолчанию, пользователь может сменить
        const kazakhstan = countries.find(c => c.code === 'KZ');
        if (kazakhstan && !this.registrationForm.get('countryId')?.value) {
          this.registrationForm.patchValue({ countryId: kazakhstan.id });
        }
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    if (confirmPassword && confirmPassword.hasError('passwordMismatch')) {
      confirmPassword.setErrors(null);
    }
    return null;
  }

  getFieldError(fieldName: string): string {
    const field = this.registrationForm.get(fieldName);
    if (field && field.touched && field.errors) {
      if (field.errors['required']) return this.errorRequiredText;
      if (field.errors['email']) return this.errorEmailText;
      if (field.errors['minlength']) return this.errorMinlengthText.replace('{min}', field.errors['minlength'].requiredLength);
      if (field.errors['maxlength']) return this.errorMaxlengthText.replace('{max}', field.errors['maxlength'].requiredLength);
      if (field.errors['passwordMismatch']) return this.errorPasswordMismatchText;
    }
    return '';
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  onSubmit(): void {
    if (this.registrationForm.invalid) {
      this.markFormGroupTouched(this.registrationForm);
      return;
    }

    this.submitting = true;
    this.error = null;

    const formValue = this.registrationForm.value;
    const email = formValue.email;
    const password = formValue.password;

    this.registrationService.registerSupplier({
      token: this.token || undefined,
      email,
      fullName: formValue.fullName,
      phone: formValue.phone || undefined,
      password,
      companyName: formValue.companyName,
      countryId: formValue.countryId
    }).subscribe({
      next: () => {
        // Аккаунт создан — сразу входим тем же логином/паролем и переходим в кабинет поставщика.
        this.authService.signIn({ username: email, password }).subscribe({
          next: () => {
            this.submitting = false;
            this.router.navigate(['/supplier/dashboard']);
          },
          error: () => {
            this.submitting = false;
            this.error = this.errorAutoLoginFailedText;
            this.cdr.markForCheck();
          }
        });
      },
      error: (err) => {
        this.submitting = false;
        this.error = err.error?.message || this.errorRegistrationDefaultText;
        this.cdr.markForCheck();
      }
    });
  }
}
